import { commonActionsReducer } from '../../../common/actions';
import { SUBCATEGORIES } from '../../../common/categories';
import { buildField } from '../../../common/utils';
import {
    AccountMatchingStateValues,
    UpdateEnrollmentSettingsAction,
    UserManagementActionTypes,
    UserManagementReducerState,
    UserManagementSettingsAction
} from './types';
import { ACCOUNT_MATCHING_FIELDS } from '../../../common/fields';
import { StoreState } from '../types';
import { AccountMatchingStrategyType } from '../../../../../types/AccountMatchingStrategyType';

const { ACCOUNT_MATCHING, ENROLLMENT_SETTINGS } = SUBCATEGORIES;

export const fieldsReducer = (
    state: UserManagementReducerState,
    action: UserManagementSettingsAction | UpdateEnrollmentSettingsAction
) => {
    const { ...newState } = state;
    switch (action.type) {
        case UserManagementActionTypes.UPDATE_ACCOUNT_MATCHING_SETTINGS:
            const { accountMatching, isTokenSharingEnabled, adFieldsConfig, isCaseSensitive } = action.payload;

            newState.accountMatching.fields.accountMatching.value = accountMatching;
            newState.accountMatching.fields.adFieldsConfig.value = adFieldsConfig;
            newState.accountMatching.fields.isCaseSensitive.value = isCaseSensitive;
            newState.accountMatching.fields.isTokenSharingEnabled.value = isTokenSharingEnabled;

            return newState;
        case UserManagementActionTypes.UPDATE_ENROLLMENT_SETTINGS:
            const { UserTokensLimit, BlockUnpairedUsers, SendNewTokenEmail } = action.payload;
            if (UserTokensLimit !== undefined) {
                newState.enrollmentSettings.fields.UserTokensLimit.value = UserTokensLimit;
            }
            if (BlockUnpairedUsers !== undefined) {
                newState.enrollmentSettings.fields.BlockUnpairedUsers.value = BlockUnpairedUsers;
            }
            if (SendNewTokenEmail !== undefined) {
                newState.enrollmentSettings.fields.SendNewTokenEmail.value = SendNewTokenEmail;
            }
            return newState;
        default:
            return commonActionsReducer(state, action, initFieldsState);
    }
};

const getAccountMatchingInitialFieldState = (
    shouldOverrideManualMatches: boolean,
    accountMatchingStrategy?: AccountMatchingStrategyType
): AccountMatchingStateValues['accountMatching'] => {
    if (!accountMatchingStrategy) {
        return {
            shouldOverrideManualMatches: false,
            isAccountMatchingEnabled: false
        };
    }

    return {
        shouldOverrideManualMatches,
        isAccountMatchingEnabled: true
    };
};

const getAdFieldsConfigInitialFieldState = (
    accountMatchingStrategy?: AccountMatchingStrategyType
): AccountMatchingStateValues['adFieldsConfig'] => {
    if (!accountMatchingStrategy) {
        return undefined;
    }

    return {
        matchAttribute: accountMatchingStrategy.config.userId,
        primaryUserIdentifyAttribute: accountMatchingStrategy.config.isPrimary.field,
        primaryUserIdentifyValue: accountMatchingStrategy.config.isPrimary.expectedValue
    };
};

export const ENROLLMENT_SETTINGS_FIELDS = {
    MAX_TOKENS: {
        ID: 'UserTokensLimit',
        NAME: 'Maximum allowed tokens'
    },
    UNPAIRED_USER_POLICY: {
        ID: 'BlockUnpairedUsers',
        NAME: 'Unpaired User Policy'
    },
    ENROLLMENT_CONFIRMATION: {
        ID: 'SendNewTokenEmail',
        NAME: 'Enrollment Request'
    }
};
export const initFieldsState = ({ settings }: StoreState['settings']): UserManagementReducerState => {
    const {
        UserTokensLimit,
        BlockUnpairedUsers,
        SendNewTokenEmail,
        AccountMatchingStrategy,
        IsTokenSharingEnabled = false,
        shouldOverrideManualMatches = false
    } = settings;

    return {
        accountMatching: {
            id: ACCOUNT_MATCHING.ID,
            name: ACCOUNT_MATCHING.NAME,
            fields: {
                accountMatching: buildField(
                    ACCOUNT_MATCHING_FIELDS.ACCOUNT_MATCHING,
                    getAccountMatchingInitialFieldState(shouldOverrideManualMatches, AccountMatchingStrategy)
                ),
                adFieldsConfig: buildField(
                    ACCOUNT_MATCHING_FIELDS.AD_FIELDS_CONFIG,
                    getAdFieldsConfigInitialFieldState(AccountMatchingStrategy)
                ),
                isCaseSensitive: buildField(
                    ACCOUNT_MATCHING_FIELDS.IS_CASE_SENSITIVE,
                    AccountMatchingStrategy?.config?.isCaseSensitive ?? true
                ),
                isTokenSharingEnabled: buildField(
                    ACCOUNT_MATCHING_FIELDS.IS_TOKEN_SHARING_ENABLED,
                    IsTokenSharingEnabled
                )
            }
        },
        enrollmentSettings: {
            id: ENROLLMENT_SETTINGS.ID,
            name: ENROLLMENT_SETTINGS.NAME,
            fields: {
                UserTokensLimit: buildField(ENROLLMENT_SETTINGS_FIELDS.MAX_TOKENS, UserTokensLimit),
                BlockUnpairedUsers: buildField(ENROLLMENT_SETTINGS_FIELDS.UNPAIRED_USER_POLICY, BlockUnpairedUsers),
                SendNewTokenEmail: buildField(ENROLLMENT_SETTINGS_FIELDS.ENROLLMENT_CONFIRMATION, SendNewTokenEmail)
            }
        }
    };
};

export const mapToSettings = (saveAll: boolean, selectedTab: string, fields: UserManagementReducerState) => {
    const { accountMatching, adFieldsConfig, isCaseSensitive, isTokenSharingEnabled } = fields.accountMatching.fields;
    const newSettings: Record<string, unknown> = {};

    if (saveAll || selectedTab === ACCOUNT_MATCHING.ID) {
        if (accountMatching.value.isAccountMatchingEnabled && adFieldsConfig.value) {
            newSettings.AccountMatchingStrategy = {
                type: 'AdFields',
                config: {
                    isCaseSensitive: isCaseSensitive.value,
                    userId: adFieldsConfig.value.matchAttribute,
                    isPrimary: {
                        field: adFieldsConfig.value.primaryUserIdentifyAttribute,
                        expectedValue: adFieldsConfig.value.primaryUserIdentifyValue
                    }
                }
            };
        } else {
            newSettings.AccountMatchingStrategy = null;
        }
        newSettings.IsTokenSharingEnabled = isTokenSharingEnabled.value;
        newSettings.shouldOverrideManualMatches = accountMatching.value.shouldOverrideManualMatches ?? false;
    }

    if (saveAll || selectedTab === ENROLLMENT_SETTINGS.ID) {
        const { UserTokensLimit, BlockUnpairedUsers, SendNewTokenEmail } = fields.enrollmentSettings.fields;

        newSettings.UserTokensLimit = UserTokensLimit.value;
        newSettings.SendNewTokenEmail = SendNewTokenEmail.value;
        newSettings.BlockUnpairedUsers = BlockUnpairedUsers.value;
    }

    return newSettings;
};
